import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";

import {
  Switch,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import ClearIcon from "@mui/icons-material/Clear";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import EditIconBtn from "app/Components/Buttons/IconBtns/EditIconBtn";
import SaveIconBtn from "app/Components/Buttons/IconBtns/SaveIconBtn";
import CancelIconBtn from "app/Components/Buttons/IconBtns/CancelIconBtn";

import { db } from "config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import UpdatePhoneNumber from "./UpdatePhoneNumber.jsx";

interface NotificationPreferences {
  newMessage?: boolean;
  requestAccepted?: boolean;
  requestDeclined?: boolean;
  confirmationNeeded?: boolean;
  confirmationAccepted?: boolean;
  confirmationDeclined?: boolean;
}

interface HostPreferences {
  requestReceived?: boolean;
  playerConfirmed?: boolean;
  playerDeclined?: boolean;
}

interface UserPreferencesProps {
  // Add any props if needed
}

function UserPreferences(props: UserPreferencesProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const claims = useSelector((state: RootState) => state.user.token);

  const [loading, setLoading] = useState(true);
  const [userEmailPreferences, setUserEmailPreferences] = useState<NotificationPreferences>({});
  const [userTextPreferences, setUserTextPreferences] = useState<NotificationPreferences>({});
  const [hostEmailPreferences, setHostEmailPreferences] = useState<HostPreferences>({});
  const [hostTextPreferences, setHostTextPreferences] = useState<HostPreferences>({});
  const [canEdit, setEdit] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    (async function IIFE() {
      await getUserPreferences();
      await getHostPreferences();
      setLoading(false);
    })();
  }, [claims]);

  const handleChange = (key: keyof NotificationPreferences, checked: boolean) => {
    setUserEmailPreferences({ ...userEmailPreferences, [key]: checked });
  };

  const handleTextChange = (key: keyof NotificationPreferences, checked: boolean) => {
    if (user.phoneNumber) {
      setUserTextPreferences({ ...userTextPreferences, [key]: checked });
    }
  };

  const handleHostChange = (key: keyof HostPreferences, checked: boolean) => {
    setHostEmailPreferences({ ...hostEmailPreferences, [key]: checked });
  };

  const handleHostTextChange = (key: keyof HostPreferences, checked: boolean) => {
    if (user.phoneNumber) {
      setHostTextPreferences({ ...hostTextPreferences, [key]: checked });
    }
  };

  const handleAllEmailOpt = (checked: boolean) => {
    const userKeys = Object.keys(userEmailPreferences) as (keyof NotificationPreferences)[];
    const updatedUserPreferences: NotificationPreferences = {};
    
    userKeys.forEach(key => {
      updatedUserPreferences[key] = checked;
    });
    setUserEmailPreferences(updatedUserPreferences);

    if (hostEmailPreferences && Object.keys(hostEmailPreferences).length > 0) {
      const hostKeys = Object.keys(hostEmailPreferences) as (keyof HostPreferences)[];
      const updatedHostPreferences: HostPreferences = {};
      
      hostKeys.forEach(key => {
        updatedHostPreferences[key] = checked;
      });
      setHostEmailPreferences(updatedHostPreferences);
    }
  };

  const getUserPreferences = async () => {
    const userDocRef = doc(db, "users", user.uid, "data", "metadata");
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setUserEmailPreferences(data.emailNotifications);
      setUserTextPreferences(data.textNotifications);
    }
  };

  const getHostPreferences = async () => {
    if (claims && claims.isHost) {
      const hostDocRef = doc(db, "hosts", user.uid);
      const docSnap = await getDoc(hostDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setHostEmailPreferences(data.emailNotifications);
        setHostTextPreferences(data.textNotifications);
      }
    }
  };

  const UserPrefMap = {
    newMessage: "New Message Received",
    requestAccepted: "Request Accepted by Host",
    requestDeclined: "Request Declined by Host",
    confirmationNeeded: "Seat Confirmation Needed",
    confirmationAccepted: "Seat Confirmed By Host ",
    confirmationDeclined: "Seat Declined By Host",
  };

  const HostPrefMap = {
    requestReceived: "New Game Request",
    playerConfirmed: "Player Confirmed Seat at Table",
    playerDeclined: "Player Declined Seat at Table",
  };

  const savePreferences = async () => {
    try {
      setSaveError(null);
      
      await updateDoc(doc(db, "users", user.uid, "data", "metadata"), {
        emailNotifications: userEmailPreferences,
        textNotifications: userTextPreferences,
      });

      if (claims && claims.isHost) {
        await updateDoc(doc(db, "hosts", user.uid), {
          emailNotifications: hostEmailPreferences,
          textNotifications: hostTextPreferences,
        });
      }

      setEdit(false);
    } catch (error) {
      console.error("Error saving preferences:", error);
      setSaveError("Failed to save preferences. Please try again.");
    }
  };

  const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiTableHead-root': {
      backgroundColor: theme.palette.grey[50],
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  }));

  const renderUserPrefHeaders = () => (
    <TableHead>
      <TableRow>
        <TableCell>Notify When</TableCell>
        <TableCell align="center">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <EmailIcon fontSize="small" />
            <Typography variant="body2">Email</Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <SmsIcon fontSize="small" />
            <Typography variant="body2">Text</Typography>
          </Stack>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderAllNotificationsSwitch = () => {
    if (canEdit) {
      return (
        <TableRow key={`AllUserPref${canEdit}`}>
          <TableCell>
            <Stack direction="row" alignItems="center" spacing={1}>
              <NotificationsIcon color="error" />
              <Typography variant="body2" color="error" fontWeight="bold">
                All Notifications
              </Typography>
            </Stack>
          </TableCell>
          <TableCell align="center">
            <Switch
              checked
              disabled={!canEdit}
              onChange={(e) => handleAllEmailOpt(e.target.checked)}
              color="error"
            />
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" color="text.secondary">-</Typography>
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  const renderUserPreferences = () => {
    const prefKeys = Object.keys(UserPrefMap) as (keyof NotificationPreferences)[];
    return (
      <TableBody>
        {renderAllNotificationsSwitch()}
        {prefKeys.map((prefKey) => (
          <TableRow key={prefKey + canEdit}>
            <TableCell>
              <Typography variant="body2">{UserPrefMap[prefKey]}</Typography>
            </TableCell>
            <TableCell align="center">
              <Switch
                checked={userEmailPreferences[prefKey] || false}
                disabled={!canEdit}
                onChange={(e) => handleChange(prefKey, e.target.checked)}
                color={canEdit ? "primary" : "default"}
              />
            </TableCell>
            <TableCell align="center">
              {prefKey === "confirmationNeeded" ? (
                user.phoneNumber ? (
                  <Switch
                    checked={userTextPreferences[prefKey] || false}
                    disabled={!canEdit}
                    onChange={(e) => handleTextChange(prefKey, e.target.checked)}
                    color={canEdit ? "primary" : "default"}
                  />
                ) : (
                  <Chip
                    label="Phone Required"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )
              ) : (
                <ClearIcon color="disabled" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderHostPreferences = () => {
    const prefKeys = Object.keys(HostPrefMap) as (keyof HostPreferences)[];
    return (
      <TableBody>
        {prefKeys.map((prefKey) => (
          <TableRow key={prefKey + canEdit}>
            <TableCell>
              <Typography variant="body2">{HostPrefMap[prefKey]}</Typography>
            </TableCell>
            <TableCell align="center">
              <Switch
                checked={hostEmailPreferences[prefKey] || false}
                disabled={!canEdit}
                onChange={(e) => handleHostChange(prefKey, e.target.checked)}
                color={canEdit ? "primary" : "default"}
              />
            </TableCell>
            <TableCell align="center">
              {prefKey === "playerConfirmed" ? (
                user.phoneNumber ? (
                  <Switch
                    checked={hostTextPreferences[prefKey] || false}
                    disabled={!canEdit}
                    onChange={(e) => handleHostTextChange(prefKey, e.target.checked)}
                    color={canEdit ? "primary" : "default"}
                  />
                ) : (
                  <Chip
                    label="Phone Required"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )
              ) : (
                <ClearIcon color="disabled" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const EditSaveBTN = () => {
    if (canEdit) {
      return (
        <Stack direction="row" spacing={2} justifyContent="center">
          <SaveIconBtn action={savePreferences} />
          <CancelIconBtn
            action={async () => {
              setEdit(false);
              setSaveError(null);
              await getUserPreferences();
              await getHostPreferences();
            }}
          />
        </Stack>
      );
    }
    return (
      <Stack direction="row" justifyContent="center">
        <EditIconBtn action={() => setEdit(true)} />
      </Stack>
    );
  };

  const renderPreferenceOptions = () => (
    <Stack spacing={3}>
      <EditSaveBTN />
      
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      
      {Object.keys(userEmailPreferences).length > 0 && (
        <Paper elevation={2}>
          <StyledTableContainer>
            <Table>
              {renderUserPrefHeaders()}
              {renderUserPreferences()}
            </Table>
          </StyledTableContainer>
        </Paper>
      )}

      {Object.keys(hostEmailPreferences).length > 0 && (
        <Box>
          <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            Host Preferences
          </Typography>
          <Paper elevation={2}>
            <StyledTableContainer>
              <Table>
                {renderUserPrefHeaders()}
                {renderHostPreferences()}
              </Table>
            </StyledTableContainer>
          </Paper>
        </Box>
      )}
      
      <EditSaveBTN />
    </Stack>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon color="primary" />
        Notification Preferences
      </Typography>
      
      <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle1" gutterBottom>
          Phone Number Settings
        </Typography>
        <UpdatePhoneNumber />
        {!user.phoneNumber && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Add a phone number to receive SMS notifications for important updates.
          </Alert>
        )}
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        renderPreferenceOptions()
      )}
    </Box>
  );
}

export default UserPreferences;
